import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import '../css/LinkList.css';

const PAGE_SIZE = 8;

const LinkList = ({ links, onLinkClick, onRefresh }) => {
  const [copiedId, setCopiedId] = useState(null);
  const [shareStatus, setShareStatus] = useState({});
  const [shareAccounts, setShareAccounts] = useState([]);
  const [shareTarget, setShareTarget] = useState('all');
  const [page, setPage] = useState(1);
  const [longUrl, setLongUrl] = useState('');
  const [customAlias, setCustomAlias] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://urlshortener-serverside.onrender.com/api';
  const FB2_ENABLED = String(process.env.REACT_APP_FB2_CONNECT_ENABLED || '').trim() === '1';
  const FB2_PROVIDER = process.env.REACT_APP_FB2_PROVIDER || 'facebook2';
  const FB2_LABEL = process.env.REACT_APP_FB2_LABEL || 'Connect Second Facebook App';

  const totals = useMemo(() => {
    const totalClicks = links.reduce((sum, link) => sum + (link.clicks || 0), 0);
    const activeLinks = links.length;
    return { totalClicks, activeLinks };
  }, [links]);

  const totalPages = Math.max(1, Math.ceil(links.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const pageLinks = links.slice(start, start + PAGE_SIZE);

  useEffect(() => {
    const username = localStorage.getItem('username') || '';
    if (!username) return;
    const fetchAccounts = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/social/status?username=${encodeURIComponent(username)}`,
          { headers: { 'x-user': username } }
        );
        const accounts = response.data?.accounts || [];
        setShareAccounts(accounts.filter((account) => account.provider === 'facebook'));
      } catch (error) {
        setShareAccounts([]);
      }
    };
    fetchAccounts();
  }, [BASE_URL]);

  const handleCopy = async (shortUrl) => {
    const fullUrl = `${window.location.origin}/${shortUrl}`;
    await navigator.clipboard.writeText(fullUrl);
    setCopiedId(shortUrl);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const buildShareTitle = (link) => {
    const sourceUrl = link.longUrl || link.originalUrl || '';
    try {
      const parsed = new URL(sourceUrl);
      const host = parsed.hostname.replace(/^www\./, '');
      const slug = (parsed.pathname || '')
        .split('/')
        .filter(Boolean)
        .pop() || '';
      const cleanSlug = slug
        .replace(/\.[a-z0-9]+$/i, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (cleanSlug && cleanSlug.length >= 8) {
        return cleanSlug.charAt(0).toUpperCase() + cleanSlug.slice(1);
      }
      return `Link from ${host}`;
    } catch (error) {
      return 'Shared link';
    }
  };

  const openDetails = (link) => {
    if (typeof onLinkClick === 'function') {
      onLinkClick(link);
      return;
    }
    window.location.href = `/${link.shortUrl}+`;
  };

  const getCommentStatus = (results = []) => {
    const fbResults = (results || []).filter((entry) => entry?.provider === 'facebook' && entry?.ok);
    let pending = false;
    for (const entry of fbResults) {
      const comment = entry?.details?.comment;
      if (!comment) return { failure: 'Comment was not added.', pending: false };
      if (comment.pending) {
        pending = true;
        continue;
      }
      if (typeof comment === 'string') return { failure: comment, pending: false };
      if (comment.error?.message) return { failure: comment.error.message, pending: false };
      if (comment.feedError?.error?.message) return { failure: comment.feedError.error.message, pending: false };
      if (comment.photoError?.error?.message) return { failure: comment.photoError.error.message, pending: false };
      if (!comment.id) return { failure: 'Comment was not added.', pending: false };
    }
    return { failure: '', pending };
  };

  const shareToPages = async (link) => {
    const username = localStorage.getItem('username') || 'user';
    const fullShort = `${window.location.origin}/${link.shortUrl}`;
    const sourceUrl = link.longUrl || link.originalUrl || fullShort;
    const post = {
      title: buildShareTitle(link),
      url: sourceUrl,
      shortUrl: fullShort,
      category: 'News',
      subcategory: 'General',
    };
    const target =
      shareTarget !== 'all'
        ? (() => {
            const [provider, pageId] = shareTarget.split(':');
            return { provider, page_id: pageId };
          })()
        : null;

    setShareStatus((prev) => ({ ...prev, [link.shortUrl]: 'Sharing...' }));
    try {
      const resp = await axios.post(
        `${BASE_URL}/social/share-now?username=${encodeURIComponent(username)}`,
        { username, post, target, forceShare: true },
        { headers: { 'Content-Type': 'application/json', 'x-user': username } }
      );
      const results = resp?.data?.results || [];
      const ok = results.some((r) => r.ok);
      if (ok) {
        const { failure: commentFailure, pending: commentPending } = getCommentStatus(results);
        setShareStatus((prev) => ({
          ...prev,
          [link.shortUrl]: commentFailure
            ? `Shared, but comment failed: ${commentFailure}`
            : commentPending
              ? 'Shared! Comment queued.'
              : 'Shared!',
        }));
      } else {
        const firstFail = results.find((r) => !r.ok);
        const details = firstFail?.details;
        let reason = 'Share failed.';
        if (typeof details === 'string' && details.trim()) {
          reason = details;
        } else if (details?.error?.message) {
          reason = details.error.message;
        } else if (details?.feedError?.error?.message) {
          reason = details.feedError.error.message;
        } else if (details?.message) {
          reason = details.message;
        }
        setShareStatus((prev) => ({ ...prev, [link.shortUrl]: reason.slice(0, 120) }));
      }
    } catch (error) {
      const serverMsg = error?.response?.data?.error;
      setShareStatus((prev) => ({ ...prev, [link.shortUrl]: serverMsg || 'Share failed.' }));
    }
    setTimeout(() => {
      setShareStatus((prev) => ({ ...prev, [link.shortUrl]: '' }));
    }, 2500);
  };

  const connectFacebook = () => {
    const username = localStorage.getItem('username') || 'user';
    window.location.href = `${BASE_URL}/social/facebook/connect?username=${encodeURIComponent(username)}`;
  };

  const connectFacebookSecond = () => {
    const username = localStorage.getItem('username') || 'user';
    window.location.href = `${BASE_URL}/social/${FB2_PROVIDER}/connect?username=${encodeURIComponent(username)}`;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!longUrl.trim()) {
      setFormError('Long URL is required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        originalUrl: longUrl.trim(),
        creator: localStorage.getItem('username') || 'Anonymous',
      };
      if (customAlias.trim()) payload.customAlias = customAlias.trim();
      await axios.post(`${BASE_URL}/links/shorten`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      setLongUrl('');
      setCustomAlias('');
      if (onRefresh) {
        await onRefresh();
      }
      setPage(1);
    } catch (error) {
      setFormError(error.response?.data?.error || 'Failed to create link.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="links-overview">
      <form className="create-link-bar" id="create-link" onSubmit={handleCreate}>
        <input
          type="url"
          placeholder="Paste your long URL here..."
          value={longUrl}
          onChange={(e) => setLongUrl(e.target.value)}
          required
        />
        <input
          type="text"
          placeholder="Alias (optional)"
          value={customAlias}
          onChange={(e) => setCustomAlias(e.target.value)}
        />
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Shorten!'}
        </button>
      </form>
      {formError && <div className="links-error">{formError}</div>}

      <div className="links-summary-cards">
        <div className="summary-tile">
          <span>Total Clicks</span>
          <strong>{totals.totalClicks}</strong>
        </div>
        <div className="summary-tile">
          <span>Active Links</span>
          <strong>{totals.activeLinks}</strong>
        </div>
      </div>

      <div className="links-table-wrap">
        <div className="links-table-head">
          <div className="links-head-row">
            <h2>Your Shortened Links</h2>
            <div className="links-head-actions">
              <div className="share-target">
                <span>Share to</span>
                <select value={shareTarget} onChange={(e) => setShareTarget(e.target.value)}>
                  <option value="all">All connected pages</option>
                  {shareAccounts.map((account) => (
                    <option
                      key={`${account.provider}:${account.page_id || account.account_id}`}
                      value={`${account.provider}:${account.page_id || account.account_id}`}
                    >
                      {account.page_name || account.page_id}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="connect-facebook" onClick={connectFacebook}>
                Connect Facebook
              </button>
              <button
                type="button"
                className="connect-facebook secondary"
                onClick={connectFacebookSecond}
                disabled={!FB2_ENABLED}
              >
                {FB2_LABEL}
              </button>
            </div>
          </div>
          {shareAccounts.length === 0 && (
            <small className="share-hint">Connect a Facebook page to enable targeted sharing.</small>
          )}
          {!FB2_ENABLED && (
            <small className="share-hint">Second Facebook app is not active yet.</small>
          )}
        </div>
        <table className="links-table">
          <thead>
            <tr>
              <th>Short URL</th>
              <th>Original URL</th>
              <th>Clicks</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageLinks.length === 0 ? (
              <tr>
                <td colSpan="5" className="table-empty">No links yet.</td>
              </tr>
            ) : (
              pageLinks.map((link, index) => (
                <React.Fragment key={link.id || link.shortUrl}>
                  <tr>
                    <td data-label="Short URL">
                      <a
                        href={`${window.location.origin}/${link.shortUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {`bl-i.us/${link.shortUrl}`}
                      </a>
                    </td>
                    <td data-label="Original URL" className="long-cell">{link.longUrl}</td>
                    <td data-label="Clicks">{link.clicks || 0}</td>
                    <td data-label="Created">{link.createdAt ? new Date(link.createdAt).toLocaleDateString() : '—'}</td>
                    <td data-label="Actions" className="actions-cell">
                      <button onClick={() => openDetails(link)}>Details</button>
                      <button onClick={() => handleCopy(link.shortUrl)}>
                        {copiedId === link.shortUrl ? 'Copied' : 'Copy'}
                      </button>
                      <button onClick={() => shareToPages(link)}>Share to Pages</button>
                    </td>
                  </tr>
                  {shareStatus[link.shortUrl] && (
                    <tr className="table-subrow">
                      <td colSpan="5" className="share-status">
                        {shareStatus[link.shortUrl]}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="links-pagination">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>{currentPage} / {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkList;
